import { useCallback } from 'react'
import { sendErrorToSentry } from '@/utils/analytics'

export type PlanId = 'capture_monthly' | 'capture_yearly' | 'ai_monthly' | 'ai_yearly'
interface OAuthState {
  source: 'landing_page'
  plan?: PlanId
}

export const useGoogleOAuth = (selectedPlan?: PlanId) => {
  // Check if OAuth is available based on environment variables
  const isOAuthAvailable = !!(process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID && process.env.NEXT_PUBLIC_VIP_BASE_URL)

  // Default configuration for all landing page redirects
  const fallbackUrl = `${process.env.NEXT_PUBLIC_VIP_BASE_URL || 'https://vip.lazy.so'}/sign-up`

  // Create Google OAuth URL with exact VIP project format
  const createGoogleOAuthUrl = useCallback((): string => {
    const googleAuthRedirect = process.env.NEXT_PUBLIC_GOOGLE_AUTH_REDIRECT
    const googleClientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID

    if (!googleAuthRedirect || !googleClientId) {
      throw new Error('Missing required environment variables for OAuth')
    }

    const state: OAuthState = { source: 'landing_page', plan: selectedPlan ?? 'ai_yearly' }
    const params = new URLSearchParams({
      response_type: 'code',
      redirect_uri: googleAuthRedirect,
      client_id: googleClientId,
      access_type: 'offline',
      scope: 'email profile openid',
      state: JSON.stringify(state),
    })

    return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`
  }, [selectedPlan])

  // Fallback function using new tab redirect
  const redirectToFallback = useCallback(() => {
    try {
      window.open(fallbackUrl, '_blank')
    } catch (error) {
      sendErrorToSentry(error, 'Failed to open fallback redirect')
      console.error('Failed to open fallback redirect:', error)
    }
  }, [fallbackUrl])

  // Main OAuth redirect function with error handling and fallback
  const redirectToGoogleOAuth = useCallback(() => {
    try {
      if (!isOAuthAvailable) {
        console.log('OAuth not available, falling back to direct redirect')
        redirectToFallback()
        return
      }

      const oauthUrl = createGoogleOAuthUrl()
      console.log('oauthUrl', oauthUrl)

      // Use same tab navigation for OAuth
      window.location.href = oauthUrl
    } catch (error) {
      sendErrorToSentry(error, 'OAuth redirect failed, using fallback')
      console.error('OAuth redirect failed, using fallback:', error)
      redirectToFallback()
    }
  }, [isOAuthAvailable, createGoogleOAuthUrl, redirectToFallback])

  return {
    redirectToGoogleOAuth,
    redirectToFallback,
    isOAuthAvailable,
  }
}
